import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Breadcrumb} from './breadcrumb.model';
import {Subject, takeUntil} from 'rxjs';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {NgDynamicBreadcrumbService} from './ng-dynamic-breadcrumb.service';
import {ButtonModule} from "primeng/button";
import {RippleModule} from "primeng/ripple";
import {DropdownChangeEvent, DropdownModule} from "primeng/dropdown";
import {AbstractControl, FormBuilder, ReactiveFormsModule, UntypedFormGroup} from "@angular/forms";
import {DropdownList} from "../../../api/models/response/dropdown-list";
import {SharedService} from "../../services/shared/shared.service";
import {
  InternalDocumentationsViewComponent
} from "../../../modules/documentation-view/internal-documentations-view/internal-documentations-view.component";
import {breadcrumbDropdownEnum, InternalProductEnum} from "../../utils/constant/common.const";
import {AppraiserUtilsService} from "../../services/utils";
import { DefaultConstConfig } from '../../utils/constant';

@Component({
  selector: 'dynamic-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, RippleModule, DropdownModule, ReactiveFormsModule],
  exportAs: 'breadcrumb',
  templateUrl: './ng-dynamic-breadcrumb.component.html',
  styleUrl: './ng-dynamic-breadcrumb.component.scss',
})
export class NgDynamicBreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumb: Breadcrumb[] = [];
  @Input() bgColor: string = 'rgba(238,238,238,0)';
  @Input() fontSize: string = '1.1rem';
  @Input() fontColor: string = '#0275d8';
  @Input() symbol: string = ' / ';
  formGroup!: UntypedFormGroup;
  productTypeList!: Array<DropdownList> | undefined; // List of account types
  pageList!: Array<DropdownList> | undefined; // List of account types
  isDropdownVisible: boolean = false;
  selectPage: number = 0;
  isManageLearMoreDropdownVisible: boolean = false;
  @ViewChild(InternalDocumentationsViewComponent) internalDocComp!: InternalDocumentationsViewComponent;
  protected readonly breadcrumbDropdownEnum = breadcrumbDropdownEnum;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private ngDynamicBreadcrumbService: NgDynamicBreadcrumbService,
    private fb: FormBuilder,
    private router: Router,
    public _docServiceHelper: SharedService,
    private utilityDropdownService: AppraiserUtilsService,
  ) {
  }

  /**
   * Getter method for 'portalType' form control
   */
  get productTypeEnum(): AbstractControl {
    return <AbstractControl>this.formGroup.get('productTypeEnum');
  }

  /**
   * Getter method for 'portalType' form control
   */
  get page(): AbstractControl {
    return <AbstractControl>this.formGroup.get('page');
  }

  ngOnInit(): void {

    this.ngDynamicBreadcrumbService.breadcrumbLabels
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((labelData) => {
        for (const label in labelData) {
          if (labelData.hasOwnProperty(label)) {
            this.breadcrumb.map((crumb: Breadcrumb): void => {
              const labelParams =
                crumb.label.match(/[^{{]+(?=\}})/g);
              if (labelParams) {
                for (const labelParam of labelParams) {
                  const dynamicData = labelData[label];
                  if (labelParam === label) {
                    crumb.label = crumb.label.replace(
                      '{{' + labelParam + '}}',
                      dynamicData
                    );
                  }
                }
              }
            });
          }
        }
      });
    this.ngDynamicBreadcrumbService.newBreadcrumb
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((breadcrumb: Breadcrumb[]) => {
        if (breadcrumb.length > 0) {
          this.updateData(
            this.activatedRoute.snapshot.data,
            this.activatedRoute.snapshot.params,
            breadcrumb
          );
        }
      });
    this.ngDynamicBreadcrumbService.notifyRouter
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((value: any): void => {
        if (value) {
          this.updateData(value.data, value.params, null);
        }
      });
    this.initForm(); // Initialize the form
  }

  /**
   * Initialize the form group with default values
   */
  initForm(): void {
    this.formGroup = this.fb.group({
      productTypeEnum: [''],
      page: [''],
    });
    this.utilityDropdownService.utilityDropdown$.pipe().subscribe({
      next: (res) => {
        this.productTypeList = res.internalProductTypeEnums;
        this.page.setValue(1);
        const val: any = {
          value: 1,
        }
        this.getProductTypeListChange(breadcrumbDropdownEnum.Page, val)
      }
    });
    this.utilityDropdownService.pagesDropdown$.pipe().subscribe({
      next: (res) => {
        this.pageList = res.homeSteadCreditLearnMoreEnums;
        this.page.setValue(1);
        const val: any = {
          value: 1,
        }
        this.getPageListChange(breadcrumbDropdownEnum.Page, val)
      }
    });

    const currentRoute = this.router.url;
    if (currentRoute) {
      this.isDropdownVisible = currentRoute.includes(DefaultConstConfig.documentationInternalPreviewUrl);
      if (this.isDropdownVisible) {
        // Split the route into segments
        const segments = currentRoute.split('/');
        // Determine the index of the product type segment
        let productTypeIndex = segments.length - 1;
        // If there is an additional bookmark ID segment, adjust the index
        if (segments.length > 5) {
          // Adjust for cases where there's an additional bookmark ID segment
          productTypeIndex = segments.length - 2;
        }
        // Extract the product type value
        const productTypeValue = segments[productTypeIndex];
        const productTypeId = Number(productTypeValue);
        // Check and set the value from InternalProductEnum
        if (productTypeId === InternalProductEnum.InternalAnnualProcedures ||
          productTypeId === InternalProductEnum.InternalSolutions) {
          this.productTypeEnum.setValue(productTypeId);
        }
      }
    }
  }

  getProductTypeListChange(dropType: number, event: DropdownChangeEvent): void {
    if (dropType && event && event.value) {
      this._docServiceHelper.emitGetGuideGridDataList(dropType, event.value);
    }
  }
  getPageListChange(dropType: number, event: DropdownChangeEvent): void {
    if (dropType && event && event.value) {
      this.selectPage = event.value;
      this._docServiceHelper.emitGetGuideGridDataList(dropType, event.value);
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  private updateData(routeData: any, params: any, newBreadcrumb: any): void {
    const currentRoute = this.router.url;
    this.isManageLearMoreDropdownVisible = currentRoute.includes('/county-portal/manage-learn-more');
    if (routeData.breadcrumb || newBreadcrumb) {
      const data = routeData.breadcrumb
        ? routeData.breadcrumb
        : newBreadcrumb;
      const breadcrumb = JSON.parse(JSON.stringify(data));
      breadcrumb.map((crumb: any) => {
        const urlChunks = crumb.url.split('/');
        for (const chunk of urlChunks) {
          if (chunk.includes(':')) {
            const paramID = chunk.replace(':', '');
            // const routerParamID = route.snapshot.params[paramID];
            const routerParamID = params[paramID];
            crumb.url = crumb.url.replace(
              `:${paramID}`,
              routerParamID
            );
          }
        }

        const labelParams = crumb.label.match(/[^{{]+(?=\}})/g);
        if (labelParams) {
          for (const labelParam of labelParams) {
            const routerParamID = params[labelParam.trim()];
            if (routerParamID) {
              crumb.label = crumb.label.replace(
                '{{' + labelParam + '}}',
                routerParamID
              );
            }
          }
        }
        if(this.isManageLearMoreDropdownVisible){
          // this.page.setValue(this.selectPage);
          const val: any = {
            value: this.selectPage,
          }
          this.getPageListChange(breadcrumbDropdownEnum.Page, val);
        }
      });
      this.breadcrumb = breadcrumb;
    } else {
      this.breadcrumb = [];
    }
  }
}
